import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type NotesModalProps = {
  title: string;
  subtitle?: string;
  placeholder?: string;
  confirmLabel?: string;
  loading?: boolean;
  onConfirm: (_notes: string) => void;
  onClose: () => void;
};

export function NotesModal({
  title,
  subtitle,
  placeholder = 'e.g., keep it short and punchy, focus on motivation…',
  confirmLabel = 'Generate',
  loading = false,
  onConfirm,
  onClose,
}: NotesModalProps) {
  const [notes, setNotes] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onConfirm(notes);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {subtitle && (
            <p style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 14 }}>
              {subtitle}
            </p>
          )}

          <div className="form-group">
            <label>
              Guidance for AI{' '}
              <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span>
            </label>
            <textarea
              ref={textareaRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={loading}
              rows={3}
            />
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              Press Cmd/Ctrl+Enter to confirm, or leave blank to let the AI decide.
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onConfirm(notes)}
            disabled={loading}
          >
            {loading ? 'Generating…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
